/**
 * Quiz grading for ContentPlayer (engine core).
 */

import type { ContentGrader, GradingResult, ScoringConfig, QuestionCounter } from '@ai-content-hub/content-core';
import { mcqGrader, isMcqAnswered, type MCQElementData, type MCQAnswer } from '@ai-content-hub/content-elements';
import type { QuizData, QuizItem, QuizScoringConfig } from './schema';

export interface QuizItemResult {
    itemId: string;
    answer: unknown;
    isCorrect: boolean;
    score: number;
    max: number;
}

function getAllItems(data: QuizData): QuizItem[] {
    if (data.sections && data.sections.length > 0) {
        return data.sections.flatMap((s) => s.items);
    }
    return data.items ?? [];
}

function getGradeableMcqItems(data: QuizData): QuizItem[] {
    return getAllItems(data).filter((item) => item.gradeable !== false && item.type === 'mcq');
}

export function validateQuizAnswer(
    data: QuizData,
    answer: unknown,
    opts?: { allowPartial?: boolean }
): boolean {
    if (!answer || typeof answer !== 'object' || Array.isArray(answer)) {
        return false;
    }
    const rec = answer as Record<string, unknown>;
    const gradeable = getGradeableMcqItems(data);
    // Nothing gradeable: submittable only once the learner really answered an ungraded MCQ
    // (quiz with Gradeable turned off); presentation-only / untouched quiz stays not submittable.
    if (gradeable.length === 0) {
        return getAllItems(data).some((item) => item.type === 'mcq' && isMcqAnswered(rec[item.id]));
    }
    // Forced submit: grade what was answered (blanks score 0); nothing answered stays "not done".
    if (opts?.allowPartial) return gradeable.some((item) => isMcqAnswered(rec[item.id]));
    return gradeable.every((item) => isMcqAnswered(rec[item.id]));
}

export function gradeQuiz(
    data: QuizData,
    answers: Record<string, unknown>,
    scoring?: QuizScoringConfig & ScoringConfig
): GradingResult {
    const passingPercentage = scoring?.passingPercentage ?? 70;
    const gradeableItems = getGradeableMcqItems(data);

    const itemResults: QuizItemResult[] = gradeableItems.map((item) => {
        const answer = answers[item.id];
        const points = item.points || 1;

        if (item.type === 'mcq') {
            const gradeResult = mcqGrader.grade(item.data as MCQElementData, answer as MCQAnswer);
            return {
                itemId: item.id,
                answer,
                isCorrect: gradeResult.isCorrect,
                score: gradeResult.isCorrect ? points : 0,
                max: points,
            };
        }

        return {
            itemId: item.id,
            answer,
            isCorrect: false,
            score: 0,
            max: points,
        };
    });

    const totalScore = itemResults.reduce((sum, r) => sum + r.score, 0);
    const maxPoints = itemResults.reduce((sum, r) => sum + r.max, 0);
    const percentage = maxPoints > 0 ? (totalScore / maxPoints) * 100 : 0;
    const passed = percentage >= passingPercentage;
    const correctCount = itemResults.filter((r) => r.isCorrect).length;

    return {
        isCorrect: passed,
        score: totalScore,
        max: maxPoints,
        details: {
            items: itemResults,
            passed,
            timeSpentSeconds: 0,
            correctCount,
            totalItems: itemResults.length,
        },
    };
}

export const quizContentGrader: ContentGrader<QuizData, Record<string, unknown>> = {
    grade: (data, answer, cfg) => gradeQuiz(data, answer ?? {}, cfg as QuizScoringConfig),
    validateAnswer: (answer, data, opts) => validateQuizAnswer(data, answer, opts),
};

/**
 * Count gradeable MCQ items in a quiz.
 * Mirrors getGradeableMcqItems: sections > items if sections exist, else items.
 * Only counts items where gradeable !== false AND type === 'mcq'.
 */
export function countQuizQuestions(data: QuizData): number {
    return getGradeableMcqItems(data).length;
}

export const quizQuestionCounter: QuestionCounter<QuizData> = {
    countQuestions: countQuizQuestions,
};
